import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MessageSquare, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHero } from "@/components/layout/PageHero";
import { ConversationList } from "@/components/messages/ConversationList";
import { Thread } from "@/components/messages/Thread";
import { CustomOfferPay } from "@/components/messages/CustomOfferPay";
import { CATEGORIES, READ_EVENT } from "@/components/messages/unread";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useDevAuth } from "@/context/DevAuthContext";

const NewConversation = ({ initialCategory, onCreated }) => {
  const { t } = useI18n();
  const mini = useDevAuth().dev?.kind === "minisite";
  const [form, setForm] = useState({ subject: initialCategory === "custom" ? t("msg.cat.custom") : "", category: initialCategory || "technical", body: "", apps: "", eur: "" });
  const plan = form.category === "custom";
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { apps, eur, ...rest } = form;
      const { data } = await api.post("/dev/messages", plan ? { ...rest, apps: mini ? null : Number(apps), eur: eur ? Number(eur) : null } : rest);
      toast[data.notified ? "success" : "warning"](t(data.notified ? "msg.sent" : "msg.notNotified"));
      onCreated(data);
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3 p-4" data-testid="msg-new-form">
      <Input required maxLength={140} value={form.subject} onChange={set("subject")} placeholder={t("msg.subject")} aria-label={t("msg.subject")} className="bg-black/50" data-testid="msg-new-subject" />
      <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value, subject: f.subject || (e.target.value === "custom" ? t("msg.cat.custom") : "") }))} aria-label={t("msg.category")} className="w-full rounded-md border border-neutral-700 bg-black/50 px-3 py-2 text-sm text-white" data-testid="msg-new-category">
        {CATEGORIES.map((c) => <option key={c} value={c}>{t(mini && c === "custom" ? "msg.type.minisite_plan" : `msg.cat.${c}`)}</option>)}
      </select>
      {plan && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="msg-plan-fields">
          {!mini && <label className="min-w-0 text-xs text-neutral-400">{t("msg.planApps")}
            <Input required inputMode="numeric" pattern="[0-9]*" value={form.apps} onChange={set("apps")} className="mt-1 bg-black/50" data-testid="msg-plan-apps" />
          </label>}
          <label className="min-w-0 text-xs text-neutral-400">{t("msg.planEur")}
            <Input inputMode="numeric" pattern="[0-9]*" value={form.eur} onChange={set("eur")} className="mt-1 bg-black/50" data-testid="msg-plan-eur" />
          </label>
        </div>
      )}
      <Textarea required={!plan} maxLength={5000} rows={5} value={form.body} onChange={set("body")} placeholder={t(plan ? "msg.planBody" : "msg.body")} aria-label={t(plan ? "msg.planBody" : "msg.body")} className="bg-black/50" data-testid="msg-new-body" />
      <button type="submit" className="btn-red btn-sm" disabled={busy} data-testid="msg-new-send">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("msg.send")}
      </button>
    </form>
  );
};

export default function DevMessages() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState(null);
  const [conv, setConv] = useState(null);
  const prefill = CATEGORIES.includes(params.get("nouveau")) ? params.get("nouveau") : null;
  const [composing, setComposing] = useState(Boolean(prefill));
  const activeId = params.get("c");
  const load = useCallback(() => api.get("/dev/messages").then(({ data }) => setList(data)), []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!activeId) return setConv(null);
    api.get(`/dev/messages/${activeId}`).then(({ data }) => {
      setConv(data);
      window.dispatchEvent(new Event(READ_EVENT));
      load();
    }).catch(() => setConv(null));
  }, [activeId, load]);
  const open = (id) => {
    setComposing(false);
    setParams({ c: id });
  };
  const send = async (body) => {
    try {
      const { data } = await api.post(`/dev/messages/${conv.id}`, { body });
      setConv(data);
      if (!data.notified) toast.warning(t("msg.notNotified"));
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  const payAction = (m) => (m.action.type !== "pay_custom" ? null : m.action.current ? <CustomOfferPay testId={`msg-pay-${m.id}`} onPaid={() => load()} /> : <p className="mt-2 text-xs text-neutral-500">{t("custom.expired")}</p>);
  const showThread = composing || conv;
  return (
    <div data-testid="dev-messages-page">
      <PageHero eyebrow={t("dev.eyebrow")} title={t("msg.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-400">{t("msg.intro")}</p>
      </PageHero>
      <div className="mx-auto grid max-w-6xl gap-4 px-4 pb-16 sm:px-8 lg:grid-cols-[340px_1fr]">
        <section className={`card-carbon min-w-0 overflow-hidden rounded-2xl ${showThread ? "hidden lg:block" : ""}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-800 p-4">
            <h2 className="flex min-w-0 items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><MessageSquare className="h-4 w-4 text-[var(--gold)]" /> {t("msg.title")}</h2>
            <button type="button" className="btn-gold btn-sm" onClick={() => { setParams({}); setComposing(true); }} data-testid="msg-new-btn">
              <Plus className="h-4 w-4" /> {t("msg.new")}
            </button>
          </div>
          {list === null ? <Loader2 className="m-4 h-5 w-5 animate-spin text-[var(--gold)]" /> : <ConversationList items={list} activeId={activeId} onOpen={open} />}
        </section>
        <section className={`card-carbon min-w-0 overflow-hidden rounded-2xl ${showThread ? "" : "hidden lg:block"}`}>
          {composing ? (
            <NewConversation initialCategory={prefill} onCreated={(c) => { load(); open(c.id); }} />
          ) : conv ? (
            <Thread conv={conv} onSend={send} onBack={() => setParams({})} renderAction={payAction} onReload={() => api.get(`/dev/messages/${conv.id}`).then(({ data }) => setConv(data))} />
          ) : (
            <p className="p-6 text-sm text-neutral-400" data-testid="msg-select-hint">{t("msg.select")}</p>
          )}
        </section>
      </div>
    </div>
  );
}
